// pages/Reports.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  RefreshCw,
  Plus,
  FolderOpen,
  BarChart3,
  Download,
  Cloud,
  MoreVertical,
  Copy,
  Trash2,
  Edit,
  Play,
} from "lucide-react";
import { useReports } from "../../contexts/ReportContext";

const Reports = () => {
  const navigate = useNavigate();
  const {
    reports,
    folders,
    reportTypes,
    loading,
    fetchReports,
    fetchFolders,
    fetchReportTypes,
    deleteReport,
    cloneReport,
    syncFromSalesforce,
    createWallboardReports,
    createSalesReports,
    createCaseReports,
    createUpgradeReports,
  } = useReports();

  const [search, setSearch] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchReportTypes();
    fetchFolders();
    fetchReports();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      await syncFromSalesforce();
      alert("Reports synced successfully from Salesforce");
    } catch (error) {
      alert("Failed to sync reports: " + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (reportId) => {
    if (window.confirm("Are you sure you want to delete this report?")) {
      try {
        await deleteReport(reportId);
        alert("Report deleted successfully");
      } catch (error) {
        alert("Failed to delete report");
      }
    }
  };

  const handleClone = async (report) => {
    const name = prompt(
      "Enter name for cloned report:",
      `${report.name} (Copy)`,
    );
    if (name) {
      try {
        await cloneReport(report.id, name);
        alert("Report cloned successfully");
      } catch (error) {
        alert("Failed to clone report");
      }
    }
  };

  const filteredReports = reports.filter((report) => {
    if (search && !report.name.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    if (selectedFolder && report.folderId !== selectedFolder) {
      return false;
    }
    if (selectedType && report.reportType !== selectedType) {
      return false;
    }
    return true;
  });

  const getReportTypeLabel = (type) => {
    return reportTypes[type]?.label || type;
  };

  const getFormatBadge = (format) => {
    const badges = {
      tabular: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      summary: "bg-green-500/10 text-green-400 border-green-500/20",
      matrix: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    };
    return badges[format] || badges.tabular;
  };

  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Reports</h1>
          <p className="text-gray-400">
            Create and manage custom reports for your data
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <Cloud size={16} className={syncing ? "animate-spin" : ""} />
            <span>{syncing ? "Syncing..." : "Sync Salesforce"}</span>
          </button>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "This will create 12 predefined case management reports. Continue?",
                )
              ) {
                try {
                  await createCaseReports();
                  alert("Case reports created successfully!");
                } catch (error) {
                  alert("Failed to create case reports: " + error.message);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            <Plus size={16} />
            <span>Create Case Reports</span>
          </button>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "This will create 12 predefined case management reports. Continue?",
                )
              ) {
                try {
                  await createWallboardReports();
                  alert("Case reports created successfully!");
                } catch (error) {
                  alert("Failed to create case reports: " + error.message);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            <Plus size={16} />
            <span>Create Wallboard Reports</span>
          </button>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "This will create 2 predefined upgrade reports. Continue?",
                )
              ) {
                try {
                  await createUpgradeReports();
                  alert("Upgrade reports created successfully!");
                } catch (error) {
                  alert("Failed to create upgrade reports: " + error.message);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors"
          >
            <Plus size={16} />
            <span>Create Upgrade Reports</span>
          </button>
          <button
            onClick={async () => {
              if (
                window.confirm(
                  "This will create 6 predefined sales reports. Continue?",
                )
              ) {
                try {
                  await createSalesReports();
                  alert("Sales reports created successfully!");
                } catch (error) {
                  alert("Failed to create sales reports: " + error.message);
                }
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors"
          >
            <Plus size={16} />
            <span>Create Sales Reports</span>
          </button>

          <button
            onClick={() => navigate("/reports/new")}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-all"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
            }}
          >
            <Plus size={16} />
            <span>New Report</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Search */}
          <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-4">
            <div className="relative mb-4">
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search reports..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Report Type Filter */}
            <div className="mb-4">
              <label className="text-sm text-gray-400 mb-2 block">
                Report Type
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">All Types</option>
                {Object.keys(reportTypes).map((type) => (
                  <option key={type} value={type}>
                    {reportTypes[type].label}
                  </option>
                ))}
              </select>
            </div>

            {/* Folders */}
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">
                Folders
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    selectedFolder === null
                      ? "bg-blue-600 text-white"
                      : "hover:bg-gray-800 text-gray-300"
                  }`}
                >
                  <FolderOpen size={16} />
                  <span className="text-sm">All Reports</span>
                </button>
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      selectedFolder === folder.id
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-800 text-gray-300"
                    }`}
                  >
                    <FolderOpen size={16} />
                    <span className="text-sm truncate">{folder.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="animate-spin text-blue-500" size={32} />
            </div>
          ) : filteredReports.length === 0 ? (
            <div className="bg-[#0C0E12] rounded-lg border border-gray-700 p-12 text-center">
              <BarChart3 size={48} className="mx-auto mb-4 text-gray-600" />
              <h3 className="text-lg font-semibold mb-2">No reports found</h3>
              <p className="text-gray-400 mb-4">
                {search || selectedFolder || selectedType
                  ? "Try adjusting your filters"
                  : "Create your first report to get started"}
              </p>
              <button
                onClick={() => navigate("/reports/new")}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Create Report
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="bg-[#0C0E12] rounded-lg border border-gray-700 hover:border-gray-600 transition-all cursor-pointer"
                  onClick={() => navigate(`/reports/${report.id}`)}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold mb-1 truncate">
                          {report.name}
                        </h3>
                        <p className="text-xs text-gray-400 truncate">
                          {getReportTypeLabel(report.reportType)}
                        </p>
                      </div>

                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionsMenu(
                              showActionsMenu === report.id ? null : report.id,
                            );
                          }}
                          className="p-1 hover:bg-gray-800 rounded transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {showActionsMenu === report.id && (
                          <div className="absolute right-0 top-8 w-48 bg-[#13161B] border border-gray-700 rounded-lg shadow-xl z-50">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/reports/${report.id}`);
                                setShowActionsMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2 rounded-t-lg"
                            >
                              <Play size={14} />
                              Run Report
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/reports/${report.id}/edit`);
                                setShowActionsMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2"
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleClone(report);
                                setShowActionsMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2"
                            >
                              <Copy size={14} />
                              Clone
                            </button>
                            <div className="border-t border-gray-700" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(report.id);
                                setShowActionsMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2 text-red-400 rounded-b-lg"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {report.description && (
                      <p className="text-xs text-gray-400 mb-3 line-clamp-2">
                        {report.description}
                      </p>
                    )}

                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium border ${getFormatBadge(
                          report.reportFormat,
                        )}`}
                      >
                        {report.reportFormat}
                      </span>
                      {report.chartType !== "none" && (
                        <span className="px-2 py-1 rounded text-xs font-medium border bg-orange-500/10 text-orange-400 border-orange-500/20">
                          {report.chartType}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>
                        {report.lastRunAt
                          ? `Last run: ${new Date(report.lastRunAt).toLocaleDateString()}`
                          : "Never run"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Play size={12} />
                        {report.runCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
